import { Elastic, Power0, Power1, TweenMax } from 'gsap';
import { GameAudio } from '../../../Config/Audio';
import SoundManager from '../../../Sound/SoundManager';
import { Collection } from '../../../Tiles/TileSet';
import { getSpriteId } from '../../../Utils/CollectableUtils';
import { scaleToFit } from '../../../Utils/Utils';
import { Animation } from '../../Animations/Animation';
import { TileSprite } from './TileSprite';

export const ZOOM_IN_DURATION = 0.4;

export interface IWiggleArguments {
    scale: boolean;
    scaleDuration?: number;
    duration?: number;
    repeat?: number;
    repeatDelay?: number;
}

export abstract class BoardSprite extends PIXI.Sprite {
    public set isMatched(value: boolean) {
        this.matched = value;
        if (value) {
            this.killAllTweens();
        }
    }

    public get isMatched() {
        return this.matched;
    }

    public spriteId: string;
    public boardId: string;
    public locId: string;
    public isAvailable: boolean = false;
    public isSelected: boolean = false;

    protected matched: boolean = false;
    protected readonly tileHighLight: PIXI.Sprite;
    protected readonly tileOverlay: PIXI.Sprite;
    protected readonly tileBlocked: PIXI.Sprite;
    protected readonly originalWidth: number;
    protected readonly originalHeight: number;
    protected originalScale: number;
    protected wiggleAnimation: Animation;

    protected constructor(spriteId: string, tileSize: [number, number], collection: Collection) {
        super();
        this.texture = PIXI.utils.TextureCache[spriteId];
        this.anchor.set(0.5, 0.5);
        this.interactive = true;
        this.buttonMode = true;
        this.originalWidth = this.width;
        this.originalHeight = this.height;
        this.setTileSize(tileSize);

        const width = tileSize[0];
        const height = tileSize[1];

        const shadowPercent: number = 8 / 74;
        this.originalScale = scaleToFit(
            this.width - this.width * shadowPercent,
            this.height - 10,
            width,
            height
        );
        this.resetScale();

        const tileHighLight = (this.tileHighLight = new PIXI.Sprite(
            PIXI.utils.TextureCache[getSpriteId('tile-selection', collection)]
        ));
        tileHighLight.anchor.set(0.5, 0.5);
        this.tileHighLight.alpha = 0;

        this.tileOverlay = new PIXI.Sprite(
            PIXI.utils.TextureCache[getSpriteId('tile-overlay', collection)]
        );
        this.tileOverlay.anchor.set(0.5);
        this.tileOverlay.alpha = 0;

        this.tileBlocked = new PIXI.Sprite(
            PIXI.utils.TextureCache[getSpriteId('tile-blocked', collection)]
        );
        this.tileBlocked.anchor.set(0.5);
        this.tileBlocked.alpha = 0;

        this.addChild(tileHighLight, this.tileBlocked);

        this.on('pointerdown', () => {
            if (!this.matched) {
                this.emit('tileClicked', this.boardId);
            }
        });
    }

    public setTileSize(tileSize: [number, number], tween: boolean = true) {
        const targetScale = this.scaleFromSize(tileSize);
        this.originalScale = targetScale;
        TweenMax.to(this.scale, tween ? ZOOM_IN_DURATION : 0.01, {
            x: targetScale,
            y: targetScale,
            ease: Power0.easeNone,
        });
    }

    public setSelected(isSelected: boolean): void {
        if (this.isSelected === isSelected) {
            return;
        }
        this.isSelected = isSelected;
        TweenMax.killTweensOf(this.tileHighLight);

        if (this.isSelected) {
            this.playSelected();
        } else {
            this.tileHighLight.alpha = 0;
        }
    }

    public playMatch(targetPosition: [number, number]) {
        this.matched = true;
        this.killAllTweens();
        SoundManager.playSound(GameAudio.LAUNCH_TILE);
        const halfX = this.x + (targetPosition[0] - this.x) / 2;
        const halfY = this.y + (targetPosition[1] - this.y) / 2;
        TweenMax.to(this, TileSprite.TILE_MATCH_TIME / 1000, {
            bezier: {
                values: [{ x: halfX, y: halfY }, { x: targetPosition[0], y: targetPosition[1] }],
            },
            ease: Power1.easeOut,
            onComplete: () => {
                this.parent.removeChild(this);
            },
        });
    }

    public playSelected(): void {
        if (this.matched) {
            return;
        }
        SoundManager.playSound(GameAudio.TAP);
        TweenMax.to(this.scale, TileSprite.TILE_SELECT_TIME / 1000 / 2, {
            ease: Power1.easeOut,
            repeat: 1,
            x: 0.9 * this.scale.x,
            y: 0.9 * this.scale.y,
            yoyo: true,
        });
        TweenMax.to(this.tileHighLight, (TileSprite.TILE_SELECT_TIME * 2) / 1000, {
            alpha: 1,
        });
    }

    /**
     * animation played if the clicked tile is not currently available
     * triggerd on event pointerdown on tile if this.isAvailable === false
     */
    public playBlocked() {
        if (this.matched) {
            return;
        }
        TweenMax.killTweensOf(this.tileBlocked);
        this.tileBlocked.alpha = 0;
        TweenMax.to(this.tileBlocked, TileSprite.TILE_SELECT_TIME / 1500, {
            alpha: 1,
            repeat: 3,
            yoyo: true,
        });
    }

    public wiggle({
        scale,
        scaleDuration = 0.05,
        duration = 1.5,
        repeat = -1,
        repeatDelay = 0.5,
    }: IWiggleArguments) {
        if (this.wiggleAnimation) {
            this.wiggleAnimation.destroy();
            this.wiggleAnimation = null;
        }
        if (this.matched) {
            return null;
        }
        const animation = (this.wiggleAnimation = new Animation({}));
        if (scale) {
            animation.add(
                TweenMax.to(this.scale, scaleDuration / 1000, {
                    x: this.scale.x * 1.2,
                    y: this.scale.y * 1.2,
                    ease: Elastic.easeOut,
                })
            );
        }
        animation.add(
            TweenMax.to(this, duration, {
                bezier: {
                    curviness: 1.5,
                    values: [
                        { rotation: 0 },
                        { rotation: 5 * (Math.PI / 180) },
                        { rotation: -5 * (Math.PI / 180) },
                        { rotation: 0 },
                    ],
                },
                ease: Elastic.easeOut.config(1.2, 0.12),
                repeat,
                repeatDelay,
            })
        );
        animation.play();
        return animation;
    }

    public blowUp(): void {
        this.matched = true;
        this.killAllTweens();
        TweenMax.to(this, 0.2, {
            alpha: 0,
            onComplete: () => {
                this.destroy();
            },
        });
    }

    public dim(ms: number) {
        if (this.tileOverlay.parent !== this) {
            this.addChild(this.tileOverlay);
        }
        TweenMax.to(this.tileOverlay, ms / 1000, { alpha: 1 });
    }

    public resetDim() {
        this.setAvailable(this.isAvailable, false);
    }

    public revealAvailable(ms: number) {
        TweenMax.to(this.tileOverlay, ms / 1000, { alpha: 0 });
    }

    public setAvailable(isAvailable: boolean, show: boolean) {
        if (this.matched) {
            return;
        }
        this.isAvailable = isAvailable;
        if (show) {
            if (!isAvailable) {
                if (this.tileOverlay.parent !== this) {
                    this.addChild(this.tileOverlay);
                }
            }

            this.tileOverlay.alpha = isAvailable ? 0 : 1;
        } else {
            this.tileOverlay.alpha = 0;
        }
    }

    public setBoardId(id: string, loc: string) {
        this.boardId = id;
        this.locId = loc;
    }

    public resetScale() {
        this.scale.set(this.originalScale);
    }

    public setTypeVisible(visible: boolean): void {
        this.alpha = visible ? 1 : 0;
    }

    public destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.matched = true;
        this.killAllTweens();
        return super.destroy(options);
    }

    private scaleFromSize(tileSize: [number, number]) {
        const shadowPercent: number = 8 / 74;
        const targetScale = scaleToFit(
            this.originalWidth - this.originalWidth * shadowPercent,
            this.originalHeight - 10,
            tileSize[0],
            tileSize[1]
        );
        return targetScale;
    }

    private killAllTweens() {
        if (this.wiggleAnimation) {
            this.wiggleAnimation.destroy();
            this.wiggleAnimation = null;
        }

        TweenMax.killTweensOf(this);
        TweenMax.killTweensOf(this.tileHighLight);
        TweenMax.killTweensOf(this.tileBlocked);
    }
}
